import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/glucose";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Trash, Plus, Syringe, ForkKnife, Barbell, NoteBlank, Drop } from "@phosphor-icons/react";
import { toast } from "sonner";

const EVENT_TYPES = [
  { value: "Meal Bolus", icon: Syringe },
  { value: "Correction Bolus", icon: Syringe },
  { value: "Basal", icon: Drop },        // long-acting insulin (MDI)
  { value: "Carbs", icon: ForkKnife },
  { value: "Exercise", icon: Barbell },
  { value: "Note", icon: NoteBlank },
];

// Which extended nutrition fields to show. All are optional.
const NUTRITION_TYPES = new Set(["Meal Bolus", "Carbs"]);

function iconFor(type) {
  return EVENT_TYPES.find((e) => e.value === type)?.icon || NoteBlank;
}

// Local ISO strings for the date+time inputs (YYYY-MM-DD and HH:MM in the user's tz).
function toLocalDate(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function toLocalTime(ms) {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
function nowFormDate() {
  const now = Date.now();
  return { date: toLocalDate(now), time: toLocalTime(now) };
}

const EMPTY_FORM = {
  event_type: "Meal Bolus",
  insulin: "",
  carbs: "",
  protein_g: "",
  fiber_g: "",
  calories: "",
  notes: "",
  duration: "",
  ...nowFormDate(),
};

export default function Treatments() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/treatments", { params: { hours: 168 } });
      setRows(r.data || []);
    } catch {
      toast.error("Failed to load treatments");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const num = (v) => (v === "" || v == null ? null : parseFloat(v));
    // Build the epoch-ms timestamp from the date + time inputs. If either is missing,
    // fall back to "now" so we never send an invalid date.
    let dateMs = Date.now();
    if (form.date && form.time) {
      const parsed = new Date(`${form.date}T${form.time}`);
      if (!isNaN(parsed.getTime())) dateMs = parsed.getTime();
    }
    const payload = {
      event_type: form.event_type,
      insulin: num(form.insulin),
      carbs: num(form.carbs),
      protein_g: num(form.protein_g),
      fiber_g: num(form.fiber_g),
      calories: num(form.calories),
      notes: form.notes || null,
      duration: form.duration ? parseInt(form.duration, 10) : null,
      date: dateMs,
    };
    try {
      await api.post("/treatments", payload);
      toast.success("Treatment logged");
      setOpen(false);
      setForm({ ...EMPTY_FORM, ...nowFormDate() });
      load();
    } catch {
      toast.error("Failed to log treatment");
    }
  };

  // When the dialog opens, refresh the date/time to "now" so the default is always current.
  const handleOpenChange = (isOpen) => {
    if (isOpen) setForm((f) => ({ ...f, ...nowFormDate() }));
    setOpen(isOpen);
  };

  const today = toLocalDate(Date.now());

  const remove = async (id) => {
    try {
      await api.delete(`/treatments/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="space-y-6" data-testid="treatments-root">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Events log</div>
          <h1 className="font-display text-2xl sm:text-3xl tracking-tight">Treatments</h1>
        </div>
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button data-testid="add-treatment-btn" className="gap-1"><Plus size={16} /> Log event</Button>
          </DialogTrigger>
          <DialogContent data-testid="treatment-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Log treatment</DialogTitle>
              <DialogDescription>Log a new event. Adjust the date &amp; time below to backfill historical entries.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                  <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map((e) => (
                      <SelectItem key={e.value} value={e.value}>{e.value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={form.date}
                    max={today}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    data-testid="treatment-date-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={form.time}
                    onChange={(e) => setForm({ ...form, time: e.target.value })}
                    data-testid="treatment-time-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Insulin (U)</Label>
                  <Input type="number" step="0.1" value={form.insulin} onChange={(e) => setForm({ ...form, insulin: e.target.value })} data-testid="insulin-input" />
                  {form.event_type === "Basal" && (
                    <div className="text-xs text-muted-foreground">Long-acting dose (MDI). Tracked separately from bolus IOB.</div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Carbs (g)</Label>
                  <Input type="number" step="1" value={form.carbs} onChange={(e) => setForm({ ...form, carbs: e.target.value })} data-testid="carbs-input" />
                </div>
              </div>
              {NUTRITION_TYPES.has(form.event_type) && (
                <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border/60">
                  <div className="space-y-1.5">
                    <Label>Protein (g)</Label>
                    <Input type="number" step="1" value={form.protein_g} onChange={(e) => setForm({ ...form, protein_g: e.target.value })} data-testid="protein-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Fibre (g)</Label>
                    <Input type="number" step="1" value={form.fiber_g} onChange={(e) => setForm({ ...form, fiber_g: e.target.value })} data-testid="fiber-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Calories (kcal)</Label>
                    <Input type="number" step="1" value={form.calories} onChange={(e) => setForm({ ...form, calories: e.target.value })} data-testid="calories-input" />
                  </div>
                </div>
              )}
              {form.event_type === "Exercise" && (
                <div className="space-y-1.5">
                  <Label>Duration (min)</Label>
                  <Input type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="duration-input" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="submit-treatment">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Insulin</TableHead>
              <TableHead>Carbs</TableHead>
              <TableHead>Protein</TableHead>
              <TableHead>Fibre</TableHead>
              <TableHead>Calories</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10" data-testid="treatments-empty">No events yet. Log your first treatment.</TableCell></TableRow>
            )}
            {rows.map((r) => {
              const Icon = iconFor(r.event_type);
              return (
                <TableRow key={r.id} data-testid={`treatment-row-${r.id}`}>
                  <TableCell className="text-sm text-muted-foreground tabular whitespace-nowrap">
                    {new Date(r.date).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2"><Icon size={14} /> {r.event_type}</div>
                  </TableCell>
                  <TableCell className="tabular">{r.insulin != null ? `${r.insulin} U` : "—"}</TableCell>
                  <TableCell className="tabular">{r.carbs != null ? `${r.carbs} g` : "—"}</TableCell>
                  <TableCell className="tabular">{r.protein_g != null ? `${r.protein_g} g` : "—"}</TableCell>
                  <TableCell className="tabular">{r.fiber_g != null ? `${r.fiber_g} g` : "—"}</TableCell>
                  <TableCell className="tabular">{r.calories != null ? `${r.calories} kcal` : "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[300px] truncate">{r.notes || "—"}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`delete-treatment-${r.id}`}>
                      <Trash size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
